import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Amplify } from 'aws-amplify';
import { signUp, confirmSignUp, signIn, signOut, getCurrentUser, fetchAuthSession, resetPassword, confirmResetPassword } from 'aws-amplify/auth';

// Load auto-generated config (created by deploy.sh)
try {
  const { amplifyConfig } = require('../config/amplify.generated');
  Amplify.configure(amplifyConfig);
} catch (e) {
  console.warn('Amplify config not found. Run deploy.sh to generate it.');
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkUser = useCallback(async () => {
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      setUser({
        userId: currentUser.userId,
        username: currentUser.username,
        email: session.tokens?.idToken?.payload?.email || currentUser.username,
        name: session.tokens?.idToken?.payload?.name || null,
        idToken,
      });
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkUser();
  }, [checkUser]);

  const handleSignUp = async (email, password, name) => {
    const result = await signUp({
      username: email,
      password,
      options: {
        userAttributes: { email, name },
      },
    });
    return result;
  };

  const handleConfirmSignUp = async (email, code) => {
    const result = await confirmSignUp({ username: email, confirmationCode: code });
    return result;
  };

  const handleSignIn = async (email, password) => {
    const result = await signIn({ username: email, password });
    await checkUser();
    return result;
  };

  const handleSignOut = async () => {
    await signOut();
    setUser(null);
  };

  const handleForgotPassword = async (email) => {
    return await resetPassword({ username: email });
  };

  const handleConfirmResetPassword = async (email, code, newPassword) => {
    return await confirmResetPassword({ username: email, confirmationCode: code, newPassword });
  };

  const getIdToken = async () => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() || null;
    } catch {
      return null;
    }
  };

  const value = {
    user,
    loading,
    signUp: handleSignUp,
    confirmSignUp: handleConfirmSignUp,
    signIn: handleSignIn,
    signOut: handleSignOut,
    forgotPassword: handleForgotPassword,
    confirmResetPassword: handleConfirmResetPassword,
    getIdToken,
    refreshUser: checkUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
